import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Route as RouteIcon, AlertTriangle, RotateCw, ChevronDown, ChevronRight } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import ProgrammeTimeline from './ProgrammeTimeline';
import { fetchHealthProgrammes } from '../../redux/healthRecordSlice';

// THE "PROGRAMMES" SECTION OF THE HEALTH RECORD (care programmes, Round B).
// Read-only: every programme started for the person at a hospital they are
// with now — the active ones first (week, expected end, next milestone, late
// chips, every milestone), the closed ones below, collapsed, with their
// outcome. Who may read it is the server's answer (the record's own rule);
// it is NOT behind the care_programmes feature — a hospital that does not run
// programmes must not hide one started elsewhere.
function HealthProgrammesPanel({ patientId }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const state = useSelector((s) => s.healthRecord?.programmes);
    const mine = state && state.patientId === patientId;
    const [openClosed, setOpenClosed] = useState({});

    useEffect(() => {
        if (!patientId) return;
        if (mine && (state.loading || (state.loaded && !state.error))) return;
        dispatch(fetchHealthProgrammes({ patientId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, dispatch]);

    if (!mine || (state.loading && !state.loaded)) {
        return (
            <div data-testid="health-programmes-loading" className="flex flex-col items-center justify-center py-8">
                <CommonLoader />
                <span className="text-sm text-[#4ABDBD]">{t('programmes.loading')}</span>
            </div>
        );
    }

    if (state.error) {
        return (
            <div role="alert" data-testid="health-programmes-error" className="mx-auto flex max-w-xl flex-col items-center gap-2 rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
                <p className="flex items-center gap-1.5 text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    {t('programmes.record_load_failed')}
                </p>
                <button
                    type="button"
                    data-testid="health-programmes-retry"
                    onClick={() => dispatch(fetchHealthProgrammes({ patientId }))}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg"
                >
                    <RotateCw className="h-4 w-4" aria-hidden="true" />
                    {t('programmes.retry')}
                </button>
            </div>
        );
    }

    const active = state.active || [];
    const closed = state.closed || [];

    if (active.length === 0 && closed.length === 0) {
        return (
            <div data-testid="health-programmes-empty" className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                <RouteIcon className="mx-auto mb-2 h-8 w-8 text-[#9DB3B1]" aria-hidden="true" />
                <p className="text-sm text-gray-500">{t('programmes.record_empty')}</p>
            </div>
        );
    }

    return (
        <div data-testid="health-programmes" className="flex flex-col gap-4">
            <section data-testid="health-programmes-active" className="flex flex-col gap-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t('programmes.record_active')}</h4>
                {active.length === 0 ? (
                    <p className="rounded-xl bg-gray-50 px-4 py-4 text-center text-sm text-gray-400">{t('programmes.record_none_active')}</p>
                ) : active.map((p) => (
                    <ProgrammeTimeline key={p.patientProgrammeId} programme={p} scope="record" />
                ))}
            </section>
            {closed.length > 0 && (
                <section data-testid="health-programmes-closed" className="flex flex-col gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t('programmes.record_closed')}</h4>
                    {closed.map((p) => {
                        const open = Boolean(openClosed[p.patientProgrammeId]);
                        return (
                            <ProgrammeTimeline
                                key={p.patientProgrammeId}
                                programme={p}
                                scope="record"
                                compact={!open}
                                headerActions={(
                                    <button
                                        type="button"
                                        data-testid={`health-programmes-expand-${p.patientProgrammeId}`}
                                        aria-expanded={open}
                                        onClick={() => setOpenClosed((prev) => ({ ...prev, [p.patientProgrammeId]: !open }))}
                                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10"
                                    >
                                        {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                                        {open ? t('programmes.hide_milestones') : t('programmes.show_milestones')}
                                    </button>
                                )}
                            />
                        );
                    })}
                </section>
            )}
        </div>
    );
}

export default HealthProgrammesPanel;
